import React from 'react';
import { CostIcon } from '@hero/web-icons';
import type { MockOrder, MockViewerRole } from '../types';
import { EditAffordance } from './EditAffordance';

export interface PriceCardProps {
  order: MockOrder;
  viewer: MockViewerRole;
}

const formatMoney = (amount: number, currency: string) =>
  `${amount.toLocaleString('en-US')} ${currency}`;

const sourceLabel: Record<'agreement' | 'custom', string> = {
  agreement: 'Agreement',
  custom: 'Custom',
};

const sourcePillClass: Record<'agreement' | 'custom', string> = {
  agreement: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  custom: 'bg-amber-50 text-amber-700 ring-amber-200',
};

/**
 * Buy/sell legs of the chain. Each leg is a `buyer pays seller` row
 * tagged with its pricing source (agreement vs custom). For 2+ leg
 * chains we surface the broker margin between the first and last
 * leg as a derived footer row — that's the "where am I making
 * money?" answer at a glance for an intermediary viewer.
 *
 * Edit affordance: only the shipper can edit price (mock rule).
 * In production this gates on whether the viewer is on the buyer
 * or seller side of the leg they're looking at.
 */
export const PriceCard: React.FC<PriceCardProps> = ({ order, viewer }) => {
  const canEditPrice = viewer === 'shipper' || viewer === 'broker';
  const legs = order.legs;

  if (legs.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <Header />
        <div className="mt-3 flex items-start gap-3 rounded-lg bg-gray-50 p-3">
          <div className="text-sm text-gray-700">
            <p className="font-medium">Pricing not set yet</p>
            <p className="mt-0.5 text-xs text-gray-500">
              A price will appear once a contract is struck for this order.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const margin =
    legs.length > 1 ? legs[0].amount - legs[legs.length - 1].amount : undefined;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <Header />
      <div className="mt-3 divide-y divide-gray-100">
        {legs.map((leg, idx) => (
          <div
            key={`${leg.buyer}-${leg.seller}-${idx}`}
            className="flex items-center justify-between py-2.5">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[13px] text-gray-700">
                <span className="font-medium text-gray-900 truncate">
                  {leg.buyer}
                </span>
                <span className="text-gray-400">pays</span>
                <span className="font-medium text-gray-900 truncate">
                  {leg.seller}
                </span>
              </div>
              <div className="mt-1">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${sourcePillClass[leg.pricingSource]}`}>
                  {sourceLabel[leg.pricingSource]}
                </span>
              </div>
            </div>
            <div className="flex items-center text-end">
              <span className="text-[14px] font-semibold tabular-nums text-gray-900">
                {formatMoney(leg.amount, leg.currency)}
              </span>
              <EditAffordance canEdit={canEditPrice} field={`leg ${idx + 1} price`} />
            </div>
          </div>
        ))}

        {margin !== undefined && (
          <div className="flex items-center justify-between py-2.5 bg-blue-50/40 -mx-4 px-4 mt-1">
            <div className="text-[13px] font-medium text-blue-900">
              Broker margin
              <span className="ms-1 text-[11px] font-normal text-blue-700/70">
                (top leg − bottom leg)
              </span>
            </div>
            <span className="text-[14px] font-semibold tabular-nums text-blue-900">
              {formatMoney(margin, legs[0].currency)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const Header: React.FC = () => (
  <div className="flex items-center gap-2">
    <div className="rounded-lg bg-green-50 p-1.5">
      <CostIcon className="size-4 text-green-600" />
    </div>
    <h4 className="text-[14px] font-semibold text-gray-900">Pricing</h4>
  </div>
);
